import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaHouseUser, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaChevronRight, FaCheckCircle } from 'react-icons/fa';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const PROGRAM_YEARS = ['Year 1', 'Year 2', 'Year 3'];

const emptyFamilyForm = () => ({
  familyCode: '', villageName: '', tehsilDistrict: '', stateUT: '',
  numberOfMembers: '', headOfFamilyAgeGender: '', ashaWorkerName: '', mentorName: '',
  adoptedDate: new Date().toISOString().slice(0, 10), knownHealthIssues: ''
});

const emptyVisitForm = () => ({
  visitDate: new Date().toISOString().slice(0, 10), programYear: 'Year 1',
  purpose: '', findings: '', healthEducationGiven: '', referralMade: '', reflection: ''
});

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const UGFap = ({ onBack }) => {
  const [view, setView] = useState('families'); // 'families' | 'family-detail'
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const [familyModalEntry, setFamilyModalEntry] = useState(null);
  const [familyForm, setFamilyForm] = useState(emptyFamilyForm());
  const [visitModalEntry, setVisitModalEntry] = useState(null);
  const [visitForm, setVisitForm] = useState(emptyVisitForm());
  const [submitting, setSubmitting] = useState(false);
  const [visitPage, setVisitPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchFamilies = useCallback(async () => {
    try {
      setFamiliesLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/families/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setFamilies(data.families || []);
    } catch (err) {
      console.error('Error fetching FAP families:', err);
    } finally {
      setFamiliesLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchFamilies(); }, [fetchFamilies]);

  const fetchVisits = useCallback(async (familyId) => {
    try {
      setVisitsLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/visits/mine?familyId=${familyId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setVisits(data.visits || []);
    } catch (err) {
      console.error('Error fetching FAP visits:', err);
    } finally {
      setVisitsLoading(false);
    }
  }, [authHeaders]);

  const openFamily = (family) => {
    setSelectedFamily(family);
    setView('family-detail');
    setVisitPage(1);
    fetchVisits(family._id);
  };

  const backToFamilies = () => {
    setView('families');
    setSelectedFamily(null);
    fetchFamilies();
  };

  // ---- Family CRUD ----
  const openNewFamily = () => { setFamilyForm(emptyFamilyForm()); setFamilyModalEntry('new'); };
  const openEditFamily = (family) => {
    setFamilyForm({
      familyCode: family.familyCode,
      villageName: family.villageName,
      tehsilDistrict: family.tehsilDistrict || '',
      stateUT: family.stateUT || '',
      numberOfMembers: family.numberOfMembers || '',
      headOfFamilyAgeGender: family.headOfFamilyAgeGender || '',
      ashaWorkerName: family.ashaWorkerName || '',
      mentorName: family.mentorName || '',
      adoptedDate: new Date(family.adoptedDate).toISOString().slice(0, 10),
      knownHealthIssues: family.knownHealthIssues || ''
    });
    setFamilyModalEntry(family);
  };

  const submitFamily = async () => {
    if (!familyForm.familyCode.trim() || !familyForm.villageName.trim()) {
      alert('Please fill in Family code and Village name before saving.');
      return;
    }
    const isEdit = familyModalEntry && familyModalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/families${isEdit ? `/${familyModalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...familyForm, numberOfMembers: familyForm.numberOfMembers === '' ? undefined : Number(familyForm.numberOfMembers) })
      });
      const data = await res.json();
      if (data.success) {
        setFamilyModalEntry(null);
        await fetchFamilies();
      } else {
        alert(data.message || 'Failed to save family');
      }
    } catch (err) {
      console.error('Error saving FAP family:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const removeFamily = async (family) => {
    if (!window.confirm(`Delete "${family.familyCode}" and all its visit records? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/families/${family._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchFamilies();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting FAP family:', err);
    }
  };

  // ---- Visit CRUD ----
  const openNewVisit = () => { setVisitForm(emptyVisitForm()); setVisitModalEntry('new'); };
  const openEditVisit = (visit) => {
    setVisitForm({
      visitDate: new Date(visit.visitDate).toISOString().slice(0, 10),
      programYear: visit.programYear,
      purpose: visit.purpose,
      findings: visit.findings || '',
      healthEducationGiven: visit.healthEducationGiven || '',
      referralMade: visit.referralMade || '',
      reflection: visit.reflection || ''
    });
    setVisitModalEntry(visit);
  };

  const submitVisit = async () => {
    if (!visitForm.purpose.trim()) {
      alert('Please fill in Purpose before saving.');
      return;
    }
    const isEdit = visitModalEntry && visitModalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/visits${isEdit ? `/${visitModalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...visitForm, familyId: selectedFamily._id })
      });
      const data = await res.json();
      if (data.success) {
        setVisitModalEntry(null);
        await fetchVisits(selectedFamily._id);
      } else {
        alert(data.message || 'Failed to save visit');
      }
    } catch (err) {
      console.error('Error saving FAP visit:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const removeVisit = async (visit) => {
    if (!window.confirm('Delete this visit record? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/visits/${visit._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchVisits(selectedFamily._id);
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting FAP visit:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // visit alone does not flip its review status back to pending.
  const resubmitVisit = async (visit) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/fap/visits/${visit._id}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchVisits(selectedFamily._id);
      else alert(data.message || 'Failed to resubmit');
    } catch (err) {
      console.error('Error resubmitting FAP visit:', err);
    }
  };

  if (view === 'family-detail' && selectedFamily) {
    return (
      <main className="ug-main">
        <button className="ug-back-btn" onClick={backToFamilies}><FaChevronLeft /> Back to Families</button>
        <h2 className="ug-section-title"><FaHouseUser /> {selectedFamily.familyCode}</h2>
        <div className="ug-submission-meta" style={{ marginBottom: 14 }}>
          <span>Village: {selectedFamily.villageName}</span>
          {selectedFamily.tehsilDistrict && <span>Tehsil/District: {selectedFamily.tehsilDistrict}</span>}
          {selectedFamily.stateUT && <span>State/UT: {selectedFamily.stateUT}</span>}
          {selectedFamily.numberOfMembers && <span>Members: {selectedFamily.numberOfMembers}</span>}
          <span>Adopted: {formatDate(selectedFamily.adoptedDate)}</span>
        </div>

        <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNewVisit}>
          <FaPlus /> Add visit
        </button>

        {visitsLoading ? (
          <div className="ug-due-empty">Loading visits...</div>
        ) : visits.length === 0 ? (
          <div className="ug-due-empty">No visits logged yet for this family.</div>
        ) : (
          <>
          <div className="ug-reflection-list">
            {visits.slice((visitPage - 1) * ITEMS_PER_PAGE, visitPage * ITEMS_PER_PAGE).map(v => (
              <div key={v._id} className="ug-reflection-card">
                <div className="ug-reflection-card-top">
                  <span className="ug-reflection-type-badge ug-reflection-type-monthly">{v.programYear}</span>
                  <span className="ug-reflection-date">{formatDate(v.visitDate)}</span>
                  {v.review?.status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>}
                  {v.review?.status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
                </div>
                <h4 className="ug-reflection-period">{v.purpose}</h4>

                {v.findings && (<><div className="ug-reflection-section-label">Findings</div><p className="ug-reflection-text">{v.findings}</p></>)}
                {v.healthEducationGiven && (<><div className="ug-reflection-section-label">Health Education Given</div><p className="ug-reflection-text">{v.healthEducationGiven}</p></>)}
                {v.referralMade && (<><div className="ug-reflection-section-label">Referral Made</div><p className="ug-reflection-text">{v.referralMade}</p></>)}
                {v.reflection && (<><div className="ug-reflection-section-label">Reflection</div><p className="ug-reflection-text">{v.reflection}</p></>)}
                {(v.review?.feedback || v.facultyFeedback) && (<><div className="ug-reflection-section-label">Faculty Feedback</div><p className="ug-reflection-text ug-reflection-faculty-feedback">{v.review?.feedback || v.facultyFeedback}</p></>)}
                {v.review?.reviewerName && (
                  <p className="ug-reviewer-tag">
                    {v.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {v.review.reviewerName}
                  </p>
                )}

                <div className="ug-reflection-card-actions">
                  {v.review?.status !== 'approved' && (
                    <>
                      <button className="ug-reflection-icon-btn" onClick={() => openEditVisit(v)} title="Edit"><FaPencilAlt /></button>
                      <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => removeVisit(v)} title="Delete"><FaTrash /></button>
                    </>
                  )}
                  {v.review?.status === 'needs_revision' && (
                    <button className="ug-log-btn" onClick={() => resubmitVisit(v)}>Resubmit for review</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination currentPage={visitPage} totalCount={visits.length} onPageChange={setVisitPage} />
          </>
        )}

        {visitModalEntry && (
          <div className="ug-modal-overlay" onClick={() => !submitting && setVisitModalEntry(null)}>
            <div className="ug-modal" onClick={e => e.stopPropagation()}>
              <div className="ug-modal-head">
                <h3>{visitModalEntry === 'new' ? 'Add visit' : 'Edit visit'}</h3>
                <FaTimes onClick={() => !submitting && setVisitModalEntry(null)} />
              </div>

              <label className="ug-form-label">Visit date</label>
              <input type="date" className="ug-form-input" value={visitForm.visitDate} onChange={e => setVisitForm({ ...visitForm, visitDate: e.target.value })} />

              <label className="ug-form-label">Program year</label>
              <select className="ug-form-input" value={visitForm.programYear} onChange={e => setVisitForm({ ...visitForm, programYear: e.target.value })}>
                {PROGRAM_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>

              <label className="ug-form-label">Purpose</label>
              <input type="text" className="ug-form-input" placeholder="e.g. Health status assessment, Health education on hypertension" value={visitForm.purpose} onChange={e => setVisitForm({ ...visitForm, purpose: e.target.value })} />

              <label className="ug-form-label">Findings (optional)</label>
              <textarea className="ug-form-input ug-form-textarea" placeholder="Physical/mental/social health status, history of illness/addiction, health issues identified" value={visitForm.findings} onChange={e => setVisitForm({ ...visitForm, findings: e.target.value })} />

              <label className="ug-form-label">Health education given (optional)</label>
              <textarea className="ug-form-input ug-form-textarea" value={visitForm.healthEducationGiven} onChange={e => setVisitForm({ ...visitForm, healthEducationGiven: e.target.value })} />

              <label className="ug-form-label">Referral made (optional)</label>
              <input type="text" className="ug-form-input" value={visitForm.referralMade} onChange={e => setVisitForm({ ...visitForm, referralMade: e.target.value })} />

              <label className="ug-form-label">Reflection (optional)</label>
              <textarea className="ug-form-input ug-form-textarea" value={visitForm.reflection} onChange={e => setVisitForm({ ...visitForm, reflection: e.target.value })} />

              <div className="ug-modal-actions">
                <button className="ug-btn-secondary" onClick={() => setVisitModalEntry(null)} disabled={submitting}>Cancel</button>
                <button className="ug-btn-primary" onClick={submitVisit} disabled={submitting}>{submitting ? 'Saving...' : 'Save visit'}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaHouseUser /> Family Adoption Programme</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Mandatory NMC CBME requirement (from the 2023-24 batch) — adopt a minimum of 3 (ideally 5) families in a village and track their health over 3 years of MBBS.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNewFamily}>
        <FaPlus /> Add adopted family
      </button>

      {familiesLoading ? (
        <div className="ug-due-empty">Loading families...</div>
      ) : families.length === 0 ? (
        <div className="ug-due-empty">No adopted families yet. Add a family to start logging visits.</div>
      ) : (
        <div className="ug-subject-grid">
          {families.map(family => (
            <div key={family._id} className="ug-subject-card" onClick={() => openFamily(family)}>
              <div className="ug-subject-card-head">
                <span className="ug-subject-code">{family.familyCode}</span>
                <FaChevronRight />
              </div>
              <div className="ug-subject-name">{family.villageName}</div>
              <div className="ug-progress-text">{family.visitCount} visit{family.visitCount === 1 ? '' : 's'} logged</div>
              <div className="ug-reflection-card-actions" onClick={e => e.stopPropagation()}>
                <button className="ug-reflection-icon-btn" onClick={() => openEditFamily(family)} title="Edit"><FaPencilAlt /></button>
                <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => removeFamily(family)} title="Delete"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {familyModalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setFamilyModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{familyModalEntry === 'new' ? 'Add adopted family' : 'Edit adopted family'}</h3>
              <FaTimes onClick={() => !submitting && setFamilyModalEntry(null)} />
            </div>
            <p className="ug-modal-hint">Use a de-identified reference (e.g. "Family 1") instead of real names, consistent with patient privacy conventions.</p>

            <label className="ug-form-label">Family code / reference</label>
            <input type="text" className="ug-form-input" placeholder='e.g. "Family 1"' value={familyForm.familyCode} onChange={e => setFamilyForm({ ...familyForm, familyCode: e.target.value })} />

            <label className="ug-form-label">Village name</label>
            <input type="text" className="ug-form-input" value={familyForm.villageName} onChange={e => setFamilyForm({ ...familyForm, villageName: e.target.value })} />

            <label className="ug-form-label">Tehsil / District (optional)</label>
            <input type="text" className="ug-form-input" value={familyForm.tehsilDistrict} onChange={e => setFamilyForm({ ...familyForm, tehsilDistrict: e.target.value })} />

            <label className="ug-form-label">State / UT (optional)</label>
            <input type="text" className="ug-form-input" value={familyForm.stateUT} onChange={e => setFamilyForm({ ...familyForm, stateUT: e.target.value })} />

            <label className="ug-form-label">Number of members (optional)</label>
            <input type="number" min="1" className="ug-form-input" value={familyForm.numberOfMembers} onChange={e => setFamilyForm({ ...familyForm, numberOfMembers: e.target.value })} />

            <label className="ug-form-label">Head of family — age/gender (optional)</label>
            <input type="text" className="ug-form-input" placeholder='e.g. "45M"' value={familyForm.headOfFamilyAgeGender} onChange={e => setFamilyForm({ ...familyForm, headOfFamilyAgeGender: e.target.value })} />

            <label className="ug-form-label">ASHA worker name (optional)</label>
            <input type="text" className="ug-form-input" value={familyForm.ashaWorkerName} onChange={e => setFamilyForm({ ...familyForm, ashaWorkerName: e.target.value })} />

            <label className="ug-form-label">Mentor name (optional)</label>
            <input type="text" className="ug-form-input" value={familyForm.mentorName} onChange={e => setFamilyForm({ ...familyForm, mentorName: e.target.value })} />

            <label className="ug-form-label">Adopted date</label>
            <input type="date" className="ug-form-input" value={familyForm.adoptedDate} onChange={e => setFamilyForm({ ...familyForm, adoptedDate: e.target.value })} />

            <label className="ug-form-label">Known health issues (optional)</label>
            <textarea className="ug-form-input ug-form-textarea" value={familyForm.knownHealthIssues} onChange={e => setFamilyForm({ ...familyForm, knownHealthIssues: e.target.value })} />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setFamilyModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submitFamily} disabled={submitting}>{submitting ? 'Saving...' : 'Save family'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGFap;
